import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Warning, UserCircle, ArrowRight } from "@phosphor-icons/react";

/**
 * Duplicate detection banner for candidate-add forms.
 *
 * Fires a debounced GET /api/candidates/duplicate-check whenever email or
 * phone change and renders one of three states:
 *   - null            → no match / not enough input
 *   - "block" (red)   → same-job hit (submit will 409)
 *   - "warn"  (amber) → same person on another posting (informational)
 *
 * Callers get an `onStatus(status)` callback so they can also disable the
 * submit button on hard-block, e.g. `setCanSubmit(status !== "block")`.
 */
const DEBOUNCE_MS = 400;
const STAGE_TONE = {
    Hired: "bg-emerald-100 text-emerald-800",
    Offer: "bg-cobalt/10 text-cobalt",
    Interview: "bg-amber-100 text-amber-800",
    Screening: "bg-slate-100 text-slate-700",
    Applied: "bg-slate-100 text-slate-700",
    Rejected: "bg-rose-100 text-rose-700",
};

export default function DuplicateCheckBanner({ email, phone, jobId, onStatus, onOpenCandidate }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const timer = useRef(null);
    const seq = useRef(0);

    const normalizedEmail = (email || "").trim().toLowerCase();
    const normalizedPhone = (phone || "").trim();

    useEffect(() => {
        const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
        const phoneOk = normalizedPhone.replace(/\D/g, "").length >= 10;
        if (!emailOk && !phoneOk) {
            setData(null);
            onStatus?.(null);
            return;
        }
        // Clear stale data as soon as inputs change so the previous match
        // doesn't linger while the new debounced lookup is pending.
        setData(null);
        onStatus?.(null);
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(async () => {
            const mine = ++seq.current;
            setLoading(true);
            try {
                const params = { job_id: jobId };
                if (emailOk) params.email = normalizedEmail;
                if (phoneOk) params.phone = normalizedPhone;
                const { data: res } = await api.get("/candidates/duplicate-check", { params });
                if (mine !== seq.current) return; // stale
                setData(res);
                onStatus?.(res?.same_job ? "block" : (res?.other_postings?.length ? "warn" : null));
            } catch {
                if (mine !== seq.current) return;
                setData(null);
                onStatus?.(null);
            } finally {
                if (mine === seq.current) setLoading(false);
            }
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer.current);
    }, [normalizedEmail, normalizedPhone, jobId, onStatus]);

    if (!data) return null;
    const { same_job, other_postings } = data;
    if (!same_job && !other_postings?.length) return null;

    // Hard block — same-job dup
    if (same_job) {
        return (
            <div
                data-testid="dup-banner-block"
                className="border border-rose-300 bg-rose-50 px-4 py-3 flex items-start gap-3"
            >
                <Warning size={20} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-rose-900 mb-1">
                        This person is already on this requirement.
                    </p>
                    {same_job.name && (
                        <p
                            data-testid="dup-banner-block-name"
                            className="text-xs text-rose-900 font-semibold mb-1 truncate"
                        >
                            {same_job.name}
                        </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-rose-800">
                        <span
                            className={`px-2 py-0.5 font-bold uppercase tracking-wider text-[10px] ${STAGE_TONE[same_job.stage] || "bg-slate-100 text-slate-700"}`}
                        >
                            {same_job.stage}
                        </span>
                        <span>·</span>
                        <span>Matched by {same_job.matched_on}</span>
                        <button
                            data-testid="dup-banner-open"
                            type="button"
                            onClick={() => onOpenCandidate?.(same_job.id)}
                            className="ml-auto font-bold text-rose-900 underline underline-offset-2 hover:no-underline inline-flex items-center gap-1"
                        >
                            Open <ArrowRight size={12} weight="bold" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Soft warn — same person, different postings
    const count = other_postings.length;
    return (
        <div
            data-testid="dup-banner-warn"
            className="border border-amber-300 bg-amber-50 px-4 py-3"
        >
            <div className="flex items-start gap-3 mb-2">
                <UserCircle size={20} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                <p className="text-sm font-bold text-amber-900">
                    Already in system —{" "}
                    <span className="tabular-nums">{count}</span>{" "}
                    active {count === 1 ? "posting" : "postings"} on other roles.
                </p>
            </div>
            <ul className="space-y-1.5 ml-8">
                {other_postings.slice(0, 4).map((p) => (
                    <li key={p.id} className="text-xs text-amber-900 flex items-center gap-2 flex-wrap">
                        <span
                            className={`px-2 py-0.5 font-bold uppercase tracking-wider text-[9px] ${STAGE_TONE[p.stage] || "bg-slate-100 text-slate-700"}`}
                        >
                            {p.stage}
                        </span>
                        <span className="font-semibold truncate">{p.job_title || "Untitled"}</span>
                        {p.client_name && (
                            <span className="text-amber-700">· {p.client_name}</span>
                        )}
                        <button
                            type="button"
                            data-testid={`dup-banner-open-${p.id}`}
                            onClick={() => onOpenCandidate?.(p.id)}
                            className="ml-auto font-bold text-amber-900 underline underline-offset-2 hover:no-underline"
                        >
                            View
                        </button>
                    </li>
                ))}
                {count > 4 && (
                    <li className="text-[11px] text-amber-800 italic">
                        + {count - 4} more
                    </li>
                )}
            </ul>
            {loading && (
                <p className="text-[10px] text-amber-700 mt-2 ml-8">Refreshing…</p>
            )}
        </div>
    );
}
